import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Camera, ImageOff, Play, Film } from "lucide-react";
import { MaskTextLoad, FadeUp } from "@/components/Reveal";
import { GALLERY } from "@/data/content";

const spanCls = {
  wide: "sm:col-span-2 aspect-[16/10]",
  tall: "row-span-2 aspect-[3/4]",
  normal: "aspect-[4/3]",
};

const ytThumb = (id) => `https://img.youtube.com/vi/${id}/hqdefault.jpg`;

export default function Gallery() {
  const [searchParams] = useSearchParams();
  const initialEdition = searchParams.get("edition");
  const [filter, setFilter] = useState(GALLERY.filters.includes(initialEdition) ? initialEdition : "All");
  const isWatch = filter === "Watch";
  const photos = filter === "All" ? GALLERY.photos : GALLERY.photos.filter((p) => p.group === filter);
  const videos = GALLERY.videos || [];
  const [active, setActive] = useState(null); // index into filtered photos
  const [activeVideo, setActiveVideo] = useState(null); // video object

  const close = () => setActive(null);
  const prev = (e) => { e.stopPropagation(); setActive((i) => (i - 1 + photos.length) % photos.length); };
  const next = (e) => { e.stopPropagation(); setActive((i) => (i + 1) % photos.length); };

  return (
    <div className="bg-cream min-h-screen">
      <section className="relative pt-40 md:pt-52 pb-14 px-5 md:px-10 grain overflow-hidden" data-testid="gallery-hero">
        <div className="absolute -top-10 right-[-4rem] h-[24rem] w-[24rem] rounded-full brand-gradient blur-[130px] opacity-20 animate-floaty" />
        <div className="relative z-10 max-w-[1400px] mx-auto">
          <p className="font-display text-xs uppercase tracking-[0.3em] text-magenta mb-6">{GALLERY.kicker}</p>
          <h1 className="font-serif font-light text-[13vw] md:text-[7.5vw] leading-[0.86] text-ink">
            <MaskTextLoad lines={["Moments we've", "lived together."]} />
          </h1>
          <FadeUp delay={0.5}>
            <p className="mt-8 max-w-2xl font-sans text-lg md:text-xl leading-relaxed text-ink/65">{GALLERY.intro}</p>
          </FadeUp>

          <FadeUp delay={0.65}>
            <div className="mt-10 flex flex-wrap gap-3" data-testid="gallery-filters">
              {GALLERY.filters.map((f) => {
                const activeTab = filter === f;
                const isWatchTab = f === "Watch";
                return (
                  <button
                    key={f}
                    onClick={() => { setFilter(f); setActive(null); setActiveVideo(null); }}
                    data-testid={`gallery-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`inline-flex items-center gap-2 rounded-full px-6 py-2.5 font-display text-xs uppercase tracking-[0.14em] font-bold transition-all duration-300 ${
                      activeTab
                        ? "brand-gradient text-white shadow-[0_0_20px_rgba(232,38,111,0.35)]"
                        : "border border-ink/20 text-ink/60 hover:border-magenta hover:text-magenta"
                    }`}
                  >
                    {isWatchTab && <Film size={14} />}
                    {f}
                  </button>
                );
              })}
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ------- WATCH (Videos) ------- */}
      {isWatch ? (
        <section className="px-5 md:px-10 pb-24 md:pb-32" data-testid="gallery-videos">
          {videos.length > 0 ? (
            <div className="max-w-[1400px] mx-auto grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {videos.map((v, i) => (
                <FadeUp key={v.id} delay={i * 0.06}>
                  <button
                    onClick={() => setActiveVideo(v)}
                    data-testid={`gallery-video-${v.id}`}
                    className={`group relative w-full ${v.portrait ? "aspect-[3/4]" : "aspect-video"} overflow-hidden rounded-3xl card-shadow cursor-pointer block bg-[#1B0812]`}
                  >
                    <img
                      src={v.poster || (v.youtubeId ? ytThumb(v.youtubeId) : "")}
                      alt={v.title}
                      className="h-full w-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1B0812]/85 via-[#1B0812]/10 to-transparent" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="grid place-items-center h-14 w-14 md:h-16 md:w-16 rounded-full brand-gradient shadow-[0_0_30px_rgba(232,38,111,0.5)] transition-transform duration-500 group-hover:scale-110">
                        <Play size={24} className="text-white translate-x-0.5" fill="currentColor" />
                      </span>
                    </div>
                    <div className="absolute inset-x-0 bottom-0 p-4 md:p-5 text-left">
                      {v.group && <span className="font-display text-[10px] uppercase tracking-[0.2em] text-coral">{v.group}</span>}
                      <p className="font-serif text-base md:text-lg text-cream leading-snug">{v.title.replace(/^[^—]+—\s*/, "")}</p>
                    </div>
                  </button>
                </FadeUp>
              ))}
            </div>
          ) : (
            <FadeUp className="max-w-[1400px] mx-auto">
              <div
                data-testid="gallery-videos-empty"
                className="rounded-3xl border border-dashed border-ink/15 bg-white/50 flex flex-col items-center justify-center gap-4 text-center px-6 py-24"
              >
                <Film size={34} className="text-ink/25" />
                <p className="font-display text-xs uppercase tracking-[0.2em] text-ink/40">Films are on the way</p>
                <p className="font-serif italic text-2xl text-ink/45 max-w-md leading-snug">
                  The stories from our gatherings are being edited into film.
                </p>
                <a
                  href={GALLERY.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="gallery-youtube-cta"
                  className="mt-2 inline-flex rounded-full border border-magenta/40 px-7 py-3 font-display text-xs uppercase tracking-[0.14em] font-bold text-magenta transition-all duration-300 hover:brand-gradient hover:text-white hover:border-transparent"
                >
                  Visit our channel
                </a>
              </div>
            </FadeUp>
          )}
        </section>
      ) : (
        /* ------- PHOTOS ------- */
        <section className="px-5 md:px-10 pb-24 md:pb-32" data-testid="gallery-grid">
          <div key={filter} className="max-w-[1400px] mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 auto-rows-auto gap-4">
            {photos.map((p, i) => (
              <FadeUp key={p.id} delay={i * 0.05} className={spanCls[p.span] || spanCls.normal}>
                <button
                  onClick={() => setActive(i)}
                  data-testid={`gallery-photo-${p.id}`}
                  className="group relative h-full w-full overflow-hidden rounded-3xl card-shadow cursor-pointer block"
                >
                  <img src={p.url} alt={p.caption} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1B0812]/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute inset-x-0 bottom-0 p-5 translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                    <Camera size={18} className="text-coral mb-2" />
                    <p className="font-sans text-sm text-cream leading-snug">{p.caption}</p>
                  </div>
                </button>
              </FadeUp>
            ))}

            {filter === "All" && Array.from({ length: GALLERY.placeholders }).map((_, i) => (
              <FadeUp key={`ph-${i}`} delay={(photos.length + i) * 0.04} className={i === 0 ? "sm:col-span-2 aspect-[16/10]" : "aspect-[4/3]"}>
                <div
                  data-testid={`gallery-placeholder-${i}`}
                  className="h-full w-full rounded-3xl border border-dashed border-ink/15 bg-white/50 flex flex-col items-center justify-center gap-3 text-center px-6"
                >
                  <ImageOff size={26} className="text-ink/25" />
                  <p className="font-display text-xs uppercase tracking-[0.2em] text-ink/40">More memories coming</p>
                  <p className="font-serif italic text-lg text-ink/40">from the next gathering</p>
                </div>
              </FadeUp>
            ))}
          </div>

          <FadeUp className="max-w-[1400px] mx-auto mt-16 text-center">
            <p className="font-serif italic font-light text-2xl md:text-4xl text-ink max-w-3xl mx-auto leading-snug">
              The best chapters of this story are still being written.
            </p>
            <Link
              to="/register"
              data-testid="gallery-register-cta"
              className="mt-8 inline-flex rounded-full brand-gradient px-9 py-4 font-display text-sm uppercase tracking-[0.14em] font-bold text-white transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(232,38,111,0.4)]"
            >
              Be in the Next One
            </Link>
          </FadeUp>
        </section>
      )}

      {/* Photo Lightbox */}
      <AnimatePresence>
        {active !== null && !isWatch && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-[70] bg-[#1B0812]/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
          >
            <button data-testid="lightbox-close" onClick={close} className="absolute top-5 right-5 text-cream/80 hover:text-cream p-2" aria-label="Close">
              <X size={30} />
            </button>
            <button data-testid="lightbox-prev" onClick={prev} className="absolute left-3 md:left-8 text-cream/70 hover:text-cream p-2" aria-label="Previous">
              <ChevronLeft size={40} />
            </button>
            <button data-testid="lightbox-next" onClick={next} className="absolute right-3 md:right-8 text-cream/70 hover:text-cream p-2" aria-label="Next">
              <ChevronRight size={40} />
            </button>
            <motion.figure
              key={photos[active].id}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-4xl w-full"
            >
              <img src={photos[active].url} alt={photos[active].caption} className="w-full max-h-[78vh] object-contain rounded-2xl" />
              <figcaption className="mt-5 text-center font-serif italic text-lg md:text-xl text-cream/85">
                {photos[active].caption}
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Video Lightbox */}
      <AnimatePresence>
        {activeVideo && (
          <motion.div
            data-testid="gallery-video-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActiveVideo(null)}
            className="fixed inset-0 z-[70] bg-[#1B0812]/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
          >
            <button
              data-testid="video-lightbox-close"
              onClick={() => setActiveVideo(null)}
              className="absolute top-5 right-5 text-cream/80 hover:text-cream p-2"
              aria-label="Close"
            >
              <X size={30} />
            </button>
            <motion.div
              key={activeVideo.id}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              onClick={(e) => e.stopPropagation()}
              className={activeVideo.portrait ? "w-auto" : "w-full max-w-5xl"}
            >
              <div className={`relative overflow-hidden rounded-2xl bg-black ${activeVideo.portrait ? "h-[72vh] aspect-[9/16] mx-auto" : "w-full aspect-video"}`}>
                {activeVideo.youtubeId ? (
                  <iframe
                    className="absolute inset-0 h-full w-full"
                    src={`https://www.youtube.com/embed/${activeVideo.youtubeId}?autoplay=1&rel=0`}
                    title={activeVideo.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <video
                    className="absolute inset-0 h-full w-full object-contain"
                    src={activeVideo.src}
                    poster={activeVideo.poster}
                    controls
                    autoPlay
                    playsInline
                  />
                )}
              </div>
              <p className="mt-5 text-center font-serif italic text-lg md:text-xl text-cream/85">{activeVideo.title}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
